import React, { Fragment } from 'react';
import moment from 'moment';

import { CopyableText } from 'modules/Common/Copy';

import { ReactComponent as PickupIcon } from 'assets/icons/pickup.svg';
import { ReactComponent as DropoffIcon } from 'assets/icons/dropoff.svg';

import { VenueLink } from 'modules/point-of-interest';
import useNonPalletizedType from 'utils/hooks/useNonPalletizedType';
import { IS_TENANT } from 'tenant/config';

const t = {
  CUSTOM_PALLET: 'Sp',
  EU_PALLET: 'Eu',
  US_PALLET: 'Us',
  MIXED_PALLET: 'Mx',
  BULK: 'Bulk',
  PARCEL: 'Parcel',
  kg: 'kg',
  cm: 'cm',
};

const stopTypeIcons = {
  PICKUP: <PickupIcon />,
  DROPOFF: <DropoffIcon />,
};

const StopCard = ({
  className,
  stop,
  hasComments,
  hasCustomLoads,
  showAggregatedLoads = false,
}) => {
  const shipperOrders = stop?.shipper_orders || [];

  const { palletText, numberOfPallets } = useNonPalletizedType(
    stop,
    stop?.real_pallets,
    stop?.load_types,
    stop?.type_pallets
  );
  if (stop && palletText) {
    stop.type_pallets = palletText;
    stop.real_pallets = numberOfPallets;
  }

  return (
    <div className={className}>
      <div className="stop">
        <div className="stop__container">
          <div className="stop__times">
            {!!stop?.time_from && !!stop?.time_to && (
              <div>
                {moment(stop?.time_from).format('HH:mm')} - {moment(stop?.time_to).format('HH:mm')}
              </div>
            )}

            {shipperOrders.map((shipperOrder) => (
              <div className="id" key={shipperOrder.id}>
                <CopyableText text={`S${shipperOrder.id}`} />
                {stop.order === 0 && (
                  <div className="release_container_provider_reference">
                    {shipperOrder.release_container_provider_reference
                      ? shipperOrder.release_container_provider_reference
                      : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="stop__address">
            <div>{stop?.address?.company_name}</div>
            <div className="hint">{stop?.address?.address_line}</div>
            <div className="hint">
              {stop?.address?.outcode}
              {!!stop?.address?.label && <> - {stop?.address?.label}</>}
            </div>
            {(!!stop?.address?.contact_name || !!stop?.address?.contact_phone) && (
              <div className="hint">
                {stop?.address?.contact_name}
                {!!stop?.address?.contact_phone && <> - {stop?.address?.contact_phone}</>}
              </div>
            )}
            <VenueLink
              gid={stop?.address?.gid}
              latitude={stop?.address?.latitude}
              longitude={stop?.address?.longitude}
              addressId={stop?.address?.id}
            />
          </div>
          {hasComments && (
            <div className="stop__comments">
              {(stop?.comments || [])
                .join('')
                .split('\n')
                .map((item, key) => {
                  return (
                    <Fragment key={key}>
                      {item}
                      <br />
                    </Fragment>
                  );
                })}
            </div>
          )}
          <div className="stop__stop-type">
            {!!stopTypeIcons[stop?.stop_type] && stopTypeIcons[stop?.stop_type]}
          </div>
          {/* DISPATCHING STOP DETAIL */}
          {IS_TENANT ? (
            <>
              {showAggregatedLoads && stop.container_iso_code && (
                <div className="stop__loads">
                  <div className="stop__load__pallets">{`1 - ${stop.container_iso_code}`}</div>
                  <div className="stop__load__pallets">
                    {stop.reference_number ? stop.reference_number : ''}
                  </div>
                  <div className="stop__load__weight">
                    {stop.total_goods_weight ? stop.total_goods_weight : 0} {t.kg}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {showAggregatedLoads && (
                <div className="stop__loads">
                  <div className="stop__load__pallets">
                    {stop.real_pallets} {t[stop.type_pallets] || stop.type_pallets}
                  </div>
                  <div className="stop__load__weight">
                    {stop.total_weight} {t.kg}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="stop__loads">
            {/* GATEKEEPING STOP DETAIL */}
            {stop?.loads?.map((load, key) => (
              <>
                {IS_TENANT ? (
                  <>
                    {load.container_iso_code && (
                      <div key={`load-${key}`} className="stop__load">
                        <div className="stop__load__pallets">
                          {`1 - ${load.container_iso_code}`}
                        </div>
                        <div className="stop__load__weight">
                          {stop.loads
                            .map((l) => (l.container_iso_code ? 0 : l.total_weight))
                            .reduce((p, n) => p + n, 0)}
                          {t.kg}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div key={`load-${key}`} className="stop__load">
                    <div className="stop__load__pallets">
                      {load.real_pallets} {t[load.pallet_type] || load.pallet_type}
                    </div>
                    {hasCustomLoads && (
                      <div className="stop__load__sizes">
                        {load.pallet_type === 'CUSTOM_PALLET' && (
                          <>
                            ({load.item_width}
                            {t.cm} &times; {load.item_length}
                            {t.cm})
                          </>
                        )}
                      </div>
                    )}
                    <div className="stop__load__weight">
                      {load.total_weight} {t.kg}
                    </div>
                  </div>
                )}
              </>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StopCard;
